// §11.3 / §9.1 — Notepad as an in-process app.
//
// It declares its file types the same way an installed manifest does (§9.2), so M5.1's registry —
// and therefore "Open with" — cannot tell a built-in from a third-party editor.
//
// **A factory rather than a constant since M57.2** (§34.3, D-541): the markdown preview needs a §14
// setting and needs to read the images a document points at, and neither is on `HangarAPI`. That
// makes Notepad a D-059 capability holder, and a holder is constructed in `App.tsx` where the whole
// list of who was granted what lives — the same move Cascade, Conquest, Intercept and Archive made.
//
// It stays **eager**: unlike Workbench's own apps (D-511) Notepad is a default opener for six file
// types, so it is fetched on the boot path whatever this file does. What the preview costs is kept
// off it by the `import()` in `Notepad.tsx` instead.
import { createRoot, type Root } from 'react-dom/client';
import type { InProcessApp, NotepadShellAccess } from '@hangar/shared';
import { COPY } from '@/copy';
import { Notepad, NOTEPAD_EXTENSIONS } from './Notepad';
import './notepad.css';

const roots = new WeakMap<HTMLElement, Root>();

export function createNotepadApp(access: NotepadShellAccess): InProcessApp {
  return {
    id: 'notepad',
    name: 'Notepad',
    icon: { glyph: 'file-text', hue: 212 },
    keywords: ['text', 'editor', 'markdown', 'notes', 'write'],
    window: { width: 760, height: 560, minWidth: 420, minHeight: 300, multiWindow: true },
    fileTypes: [
      { ext: NOTEPAD_EXTENSIONS, mime: ['text/plain', 'text/markdown', 'text/csv'], role: 'editor' },
    ],
    // §9.2 — what the Windows taskbar offers on a right-click (D-100).
    jumpList: [{ id: 'new-window', label: COPY.notepad.newWindow }],

    onCommand(commandId, api) {
      if (commandId === 'new-window') void api.launch.app('notepad');
    },

    mount(container, api) {
      const root = createRoot(container);
      roots.set(container, root);
      root.render(<Notepad api={api} access={access} />);
    },

    unmount(container) {
      const root = roots.get(container);
      roots.delete(container);
      root?.unmount();
    },
  };
}
